import ActionModel from 'cio/models/action';
import { SerializedLegacyFilterType, SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { isEmpty } from '@ember/utils';
import CampaignModel from 'cio/models/campaign';
import EnvironmentModel from 'cio/models/environment';

function filterConditions(conditions: SerializedLegacyFilter[], typesToRemove: SerializedLegacyFilterType[]) {
  return conditions.filter((condition) => {
    if (isEventOrEventAttributeCompareFilter(condition) && typesToRemove.includes('event_attribute')) {
      return false;
    }

    return !typesToRemove.includes(condition.type ?? '');
  });
}

export function removeConditionTypes(action: ActionModel, types: SerializedLegacyFilterType[]) {
  // Wait until store conditions in multi-conditions - make sure to remove those
  // too
  if (action.isWaitUntil) {
    removeMultiConditionValues(action, types);
  }

  ['conditions', 'preconditions'].forEach((attr: 'conditions' | 'preconditions') => {
    if (action[attr] == null) {
      return;
    }

    action.set(attr, filterConditions(action[attr] as SerializedLegacyFilter[], types));
  });
}

export function removeMultiConditionValues(action: ActionModel, types: SerializedLegacyFilterType[]) {
  action.set(
    'multiConditions',
    action.multiConditions.map((filters: SerializedLegacyFilter[]) => filterConditions(filters, types)),
  );
}

export function removeEventTimeConditions(action: ActionModel) {
  // If the action already had the event time condition removed, we don't need
  // to modify the values nor multiConditions anymore.
  // This can happen when copying an action that itself is already a copy, and
  // the event_time_condition_dropped warning wasn't yet removed
  if (isEmpty(action.delayEventAttr)) {
    return;
  }

  action.setProperties({
    delayEventAttr: '',
    delayEventAttrOffset: 0,
  });

  // When an event time condition is removed, we need to add an empty
  // multi_conditions path, so that the wait until branches don't break
  action.set('multiConditions', [...action.multiConditions, []]);
}

function removeSubscriptionPreferences(action: ActionModel) {
  // default to use campaign settings for the subscription preferences
  action.setProperties({
    subscriptionTopicId: null,
    sendToUnsubscribed: null,
  });
}

function isEventOrEventAttributeCompareFilter(filter: SerializedLegacyFilter) {
  return (
    filter.type === 'event_attribute' ||
    (filter.type === 'attribute_compare' &&
      (filter.field_source === 'event_attribute' || filter.value_source === 'event_attribute'))
  );
}

function findFilterWarnings(
  filter: SerializedLegacyFilter,
  context: {
    sourceCampaign: CampaignModel;
    targetCampaign: CampaignModel;
    sourceEnvironment: EnvironmentModel;
    targetEnvironment: EnvironmentModel;
  },
) {
  let { sourceCampaign, targetCampaign, sourceEnvironment, targetEnvironment } = context;
  let isCopyingFromEventToEventCampaign = sourceCampaign.isEventTriggered && targetCampaign.isEventTriggered;
  let isDifferentWorkspace = sourceEnvironment.id !== targetEnvironment.id;

  let warnings = [];
  // Event attribute conditions are:
  //
  // (a) dropped if copying to a segment campaign,
  // (b) copied, but with a warning to review, if copying to an event campaign
  if (isEventOrEventAttributeCompareFilter(filter)) {
    if (sourceCampaign.isWebhookTriggered) {
      if (targetCampaign.isWebhookTriggered) {
        warnings.push('webhook_conditions_review');
      } else {
        warnings.push('webhook_conditions_dropped');
      }
    } else {
      warnings.push(isCopyingFromEventToEventCampaign ? 'event_conditions_review' : 'event_conditions_dropped');
    }
  }

  // Segment and attribute conditions are dropped only when copying between different workspaces.
  if (isDifferentWorkspace) {
    if (filter.type === 'segments_negatable' || filter.type === 'segments') {
      warnings.push('segment_conditions');
    } else if (filter.type === 'attribute') {
      warnings.push('attribute_conditions');
    } else if (filter.type === 'message') {
      warnings.push('message_conditions');
    }
  }

  return warnings;
}

const MULTI_CONDITION_WARNING_MAP: Record<string, string> = {
  webhook_conditions_review: 'webhook_multi_conditions_review',
  webhook_conditions_dropped: 'webhook_multi_conditions_dropped',
  event_conditions_review: 'event_multi_conditions_review',
  event_conditions_dropped: 'event_multi_conditions_dropped',
  segment_conditions: 'segment_multi_conditions',
  attribute_conditions: 'attribute_multi_conditions',
  message_conditions: 'message_multi_conditions',
};

function convertConditionWarningsToMultiConditionWarnings(warnings: string[]) {
  return warnings.map((warning) => MULTI_CONDITION_WARNING_MAP[warning] ?? warning);
}

// When copying between workspaces, we drop layouts, identities, etc. and add
// warnings.  When copying within the same workspace, we drop event action
// conditions too and add appropriate warnings.
export function addWarningsToActions(
  actions: ActionModel[],
  sourceCampaign: CampaignModel,
  targetCampaign: CampaignModel,
  sourceEnvironment: EnvironmentModel,
  targetEnvironment: EnvironmentModel,
  mutateActions = true,
) {
  let isDifferentWorkspace = sourceEnvironment.id !== targetEnvironment.id;
  let isLanguageAttributeSet = targetEnvironment.hasLanguageAttribute;

  let { parentConsolidatedBranchMap } = sourceCampaign;

  sourceCampaign.ensureParsedTree();

  let allWarnings: string[] = [];

  actions.forEach((action) => {
    let warnings: string[] = [];

    // Subscription topic is not a valid option to copy when srcEnv != dstEnv
    if (isDifferentWorkspace && action.subscriptionTopicId != null) {
      warnings.push('subscription_preferences_dropped');
    }

    // Send to "All subscribed people" is not a valid option when sub center is enabled
    // We need to check that there's no topic assigned here because sendToUnsubscribed is also false when a topic is assigned
    if (
      targetEnvironment.subscriptionCenterEnabled &&
      action.sendToUnsubscribed === false &&
      action.subscriptionTopicId == null
    ) {
      warnings.push('subscription_preferences_dropped');
    }

    // Email - layout, identities are reset when changing workspace
    if (isDifferentWorkspace && action.type === 'email_action') {
      warnings.push('layout', 'identities');
    }

    // Conditions - segment_conditions, attribute_conditions
    // both as preconditions as well as actual action conditions (old-style
    // "wait until" actions, before we split them into paths)
    let preconditions = action.preconditions ?? [];
    let conditions = action.conditions ?? [];

    if (action.isCreateOrUpdatePersonAttributeAction) {
      warnings.push('create_or_update_person_review');
    }

    if (action.isCreateEventAction) {
      if (sourceCampaign.type === targetCampaign.type) {
        warnings.push('create_event_data_review');
      } else {
        warnings.push('create_event_data_dropped');
      }
    }

    if (action.isBatchUpdateAction) {
      if (sourceCampaign.type === targetCampaign.type) {
        warnings.push('batch_update_data_review');
      } else {
        warnings.push('batch_update_data_dropped');
      }
    }

    [...preconditions, ...conditions].forEach((filter: SerializedLegacyFilter) => {
      let filterWarnings = findFilterWarnings(filter, {
        sourceCampaign,
        targetCampaign,
        sourceEnvironment,
        targetEnvironment,
      });
      warnings.push(...filterWarnings);
    });

    // isPreBranchWaitUntil means multiActions == null, due to incomplete
    // back-end migration: TODO - remove it once not needed anymore
    if (action.isMultiSplitBranch || (action.isWaitUntil && !action.isPreBranchWaitUntil)) {
      action.multiConditions.forEach((filters: SerializedLegacyFilter[]) => {
        filters.forEach((filter) => {
          let filterWarnings = findFilterWarnings(filter, {
            sourceCampaign,
            targetCampaign,
            sourceEnvironment,
            targetEnvironment,
          });

          if (action.isMultiSplitBranch) {
            filterWarnings = convertConditionWarningsToMultiConditionWarnings(filterWarnings);
          }

          warnings.push(...filterWarnings);
        });
      });
    }

    if (parentConsolidatedBranchMap[action.id] != null && !isLanguageAttributeSet) {
      warnings.push('lang_attribute');
    }

    if (action.isWaitUntil) {
      if (sourceCampaign.isEventTriggered && !isEmpty(action.delayEventAttr)) {
        if (targetCampaign.isEventTriggered) {
          warnings.push('event_time_condition_review');
        } else {
          warnings.push('event_time_condition_dropped');
        }
      }
    }

    if (warnings.length === 0) {
      return;
    }

    allWarnings.push(...warnings.uniq());

    if (mutateActions) {
      action.warnings = warnings.uniq().toString();
    }
  });

  return allWarnings.uniq();
}

export function updateActionsBasedOnWarnings(actions: ActionModel[]) {
  actions.forEach((action) => {
    if ((action.warnings ?? []).length === 0) {
      return;
    }

    if (action.warnings.includes('segment_conditions')) {
      removeConditionTypes(action, ['segments_negatable', 'segments']);
    }

    if (action.warnings.includes('message_conditions')) {
      removeConditionTypes(action, ['message']);
    }

    if (action.warnings.includes('event_conditions_dropped')) {
      removeConditionTypes(action, ['event_attribute']);
    }

    if (action.warnings.includes('event_multi_conditions_dropped')) {
      removeMultiConditionValues(action, ['event_attribute']);
    }

    if (action.warnings.includes('segment_multi_conditions')) {
      removeMultiConditionValues(action, ['segments_negatable', 'segments']);
    }

    if (action.warnings.includes('message_multi_conditions')) {
      removeMultiConditionValues(action, ['message']);
    }

    if (action.warnings.includes('event_time_condition_dropped')) {
      removeEventTimeConditions(action);
    }

    if (action.warnings.includes('subscription_preferences_dropped')) {
      removeSubscriptionPreferences(action);
    }
  });
}

export function addConditionsToChildActions(actionsToCopy: ActionModel[], allActions: ActionModel[]) {
  actionsToCopy
    .filter((action) => action.isVariation)
    .forEach((childAction) => {
      let selectedParentAction = actionsToCopy.findBy('id', childAction.belongsTo('parentAction').id());
      let parentActionConditions = allActions.findBy('id', childAction.belongsTo('parentAction').id())
        ?.preconditions as SerializedLegacyFilter[];
      // If we're copying a variation, but without the parent split, copy the
      // parent split's action conditions to the variation we're copying
      if (!selectedParentAction && !isEmpty(parentActionConditions)) {
        childAction.preconditions = parentActionConditions;
      }
    });
}
